import { useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import passIcon from '../../../assets/pass.svg'
import AuthRightPanel from '../../../components/auth/AuthRightPanel'
import OnboardingCard from '../../../components/common/OnboardingCard'
import ErrorPopup from '../../../components/common/errorPopup'
import { supabase } from '../../../hooks/supabase'

export default function CreateNewPassword() {
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isErrorPopupOpen, setIsErrorPopupOpen] = useState(false)
  const [passwordsMatch, setPasswordsMatch] = useState(true)
  const navigate = useNavigate()
  const token = new URLSearchParams(window.location.search).get('token')

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (password !== confirmPassword) {
      setError('Passwords do not match')
      setIsErrorPopupOpen(true)
      return
    }
    
    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: password
      })

      if (updateError) throw updateError

      navigate({ 
        to: '/signin',
        search: {
          message: 'Password updated successfully. Please sign in with your new password.'
        }
      })
    } catch (error) {
      console.error('Update password error:', error)
      setError(error instanceof Error ? error.message : 'Failed to update password')
      setIsErrorPopupOpen(true)
    }
  }

  const leftPanelContent = (
    <div className="flex flex-col h-full">
      <div className="flex flex-col p-[16px]">
        <div className="mb-1">
          <div className="text-[32px] font-semibold text-left">Create new password</div>
          <p className="text-sm font-light text-[#9EA0A0] mb-8 text-left">Enter your new password below.</p>
          
          <form onSubmit={handleUpdatePassword} className="flex flex-col">
            <div className="space-y-3">
              <div className="relative">
                <img 
                  src={passIcon} 
                  alt="Password" 
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C5E63]"
                />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value)
                    setPasswordsMatch(confirmPassword === e.target.value)
                  }}
                  placeholder="Password"
                  className="w-full h-[40px] pl-10 pr-4 text-left rounded-[10px] text-[#222222] text-sm placeholder:text-[#9FA1A7] focus:outline-none focus:ring-1 focus:ring-[#29DC5B] focus:border-transparent shadow-[0px_0px_0px_1px_#EEEFF1_inset]"
                  required
                />
              </div>

              <div className="relative">
                <img 
                  src={passIcon} 
                  alt="Confirm Password" 
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C5E63]"
                />
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value)
                    setPasswordsMatch(e.target.value === password)
                  }}
                  placeholder="Confirm password"
                  className={`w-full h-[40px] pl-10 pr-4 text-left rounded-[10px] text-[#222222] text-sm placeholder:text-[#9FA1A7] focus:outline-none focus:ring-1 ${
                    confirmPassword && !passwordsMatch 
                      ? 'focus:ring-[#FF0000] ring-1 ring-[#FF0000]' 
                      : 'focus:ring-[#29DC5B]'
                  } focus:border-transparent shadow-[0px_0px_0px_1px_#EEEFF1_inset]`}
                  required
                />
                {confirmPassword && !passwordsMatch && (
                  <p className="absolute text-[#FF0000] text-xs mt-1">
                    Passwords do not match
                  </p>
                )}
              </div>
            </div>

            <button
              type="submit"
              disabled={!passwordsMatch || !password || !confirmPassword}
              className="w-full h-[40px] bg-[#29DC5B] mt-[28px] text-[#222222] rounded-[10px] flex items-center justify-center text-sm font-normal hover:bg-[#22c55e] transition-colors shadow-[0px_0px_0px_1px_#2325291A_inset] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Reset password
            </button>
          </form>
        </div>

        <div className="text-center text-sm font-light text-[#9FA1A7] mt-[24px]">
          Back to{' '}
          <a href="/signin" className="text-[#222222] font-light hover:underline">
            Sign in
          </a>
        </div>
      </div>

      <div className="mt-auto w-full flex justify-center">
        <div className="text-center w-[253px] text-xs text-[#9FA1A7]">
          By using Clodexa, you are agreeing to our{' '}
          <a href="/privacy" className="text-[#222222] hover:underline">privacy policy</a>
          {' '}and{' '}
          <a href="/terms" className="text-[#222222] hover:underline">terms of service</a>
        </div>
      </div>
    </div>
  )

  return (
    <div className="min-h-screen h-screen flex flex-col items-center">
      <OnboardingCard 
        leftPanel={leftPanelContent}
        rightPanel={<AuthRightPanel />}
      />

      <ErrorPopup
        isOpen={isErrorPopupOpen}
        onClose={() => setIsErrorPopupOpen(false)}
        message={error || 'An error occurred'}
      />
    </div>
  )
} 